import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import { collapseEmailWrappers } from '../utils/collapseEmailWrappers';

// Template logos saved as "../kan/uploads/x.png" break inside srcdoc iframe; point at backend origin.
const fixRelativeUploads = (html) => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!html || !backend) return html;
    const origin = backend.replace(/\/kan\/?$/, '');
    return html.replace(/(src=["'])(?:\.\.\/|\.\/)+(kan\/uploads\/)/gi, `$1${origin}/$2`);
};

const linkifyHtml = (htmlString) => {
    if (!htmlString) return '';
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, 'text/html');
        // Walk over all text nodes
        const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null, false);
        const textNodes = [];
        let node;
        while ((node = walker.nextNode())) {
            if (
                node.parentNode &&
                node.parentNode.nodeName.toLowerCase() !== 'a' &&
                node.parentNode.nodeName.toLowerCase() !== 'script' &&
                node.parentNode.nodeName.toLowerCase() !== 'style'
            ) {
                textNodes.push(node);
            }
        }

        // Mark whitespace-only paragraphs (<p>&nbsp;</p>) so they don't stack full margins
        doc.body.querySelectorAll('p').forEach((p) => {
            if (!p.children.length && !p.textContent.replace(/[\s ]/g, '')) {
                p.classList.add('blank-line');
            }
        });

        const urlRegex = /(https?:\/\/[^\s<]+)/g;

        textNodes.forEach((textNode) => {
            if (urlRegex.test(textNode.nodeValue)) {
                const fragment = document.createDocumentFragment();
                let lastIndex = 0;
                urlRegex.lastIndex = 0;

                textNode.nodeValue.replace(urlRegex, (match, p1, offset) => {
                    if (offset > lastIndex) {
                        fragment.appendChild(document.createTextNode(textNode.nodeValue.slice(lastIndex, offset)));
                    }
                    const a = document.createElement('a');
                    a.href = p1;
                    a.target = '_blank';
                    a.rel = 'noopener noreferrer';
                    a.textContent = p1;
                    fragment.appendChild(a);
                    lastIndex = offset + match.length;
                    return match;
                });

                if (lastIndex < textNode.nodeValue.length) {
                    fragment.appendChild(document.createTextNode(textNode.nodeValue.slice(lastIndex)));
                }

                if (fragment.childNodes.length > 0) {
                    textNode.parentNode.replaceChild(fragment, textNode);
                }
            }
        });
        return doc.body.innerHTML;
    } catch (e) {
        return htmlString;
    }
};

/**
 * Renders email HTML inside an iframe to completely isolate
 * the email's CSS from the parent application.
 */
const SandboxedEmail = ({ html, className, style }) => {
    const iframeRef = useRef(null);
    const processedHtml = useMemo(
        () => linkifyHtml(fixRelativeUploads(collapseEmailWrappers(html))),
        [html]
    );

    const adjustHeight = useCallback(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;
        try {
            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            if (doc && doc.body) {
                const height = doc.body.scrollHeight;
                iframe.style.height = height + 'px';
            }
        } catch (e) {
            // cross-origin guard
        }
    }, []);

    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        const handleLoad = () => {
            adjustHeight();

            // Watch for dynamic content changes (images loading, etc.)
            try {
                const doc = iframe.contentDocument || iframe.contentWindow?.document;
                if (doc && doc.body) {
                    const observer = new ResizeObserver(() => adjustHeight());
                    observer.observe(doc.body);

                    // Also handle images loading after initial render
                    const images = doc.querySelectorAll('img');
                    images.forEach((img) => {
                        if (!img.complete) {
                            img.addEventListener('load', adjustHeight);
                            img.addEventListener('error', adjustHeight);
                        }
                    });

                    return () => observer.disconnect();
                }
            } catch (e) {
                // cross-origin guard
            }
        };

        iframe.addEventListener('load', handleLoad);
        return () => iframe.removeEventListener('load', handleLoad);
    }, [html, adjustHeight]);

    // Wrap the email HTML in a minimal document with base styles
    const srcdoc = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<base target="_blank">
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  html, body {
    margin: 0;
    padding: 0;
    overflow: hidden;
    background: transparent;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    line-height: 1.65;
    color: #374151;
    font-size: 15px;
    -webkit-font-smoothing: antialiased;
  }
  img {
    max-width: 100%;
    height: auto;
    vertical-align: middle;
    border-style: none;
  }
  p { margin-top: 0; margin-bottom: 1em; }
  li > p { margin: 0; }
  p.blank-line { margin: 0; line-height: 0.8; }
  ul, ol { margin-top: 0; margin-bottom: 1em; padding-left: 1.5em; }
  li { margin-bottom: 0.35em; }
  strong, b { color: #111827; font-weight: 600; }
  hr {
    border: none;
    border-top: 1px solid #e9ecef;
    margin: 1.25em 0;
  }
  table {
    border-collapse: collapse;
    max-width: 100%;
  }
  td, th {
    padding: 4px 8px;
    vertical-align: top;
  }
  blockquote, .gmail_quote {
    // margin: 1em 0;
    // padding: 0.5em 0 0.5em 1rem;
    border-left: 3px solid #d1d9d4;
    background: #f5f7f6;
    border-radius: 0 6px 6px 0;
    color: #6b7280;
    font-size: 14px;
  }
  a {
    color: #1a7d3e;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  a:hover { color: #145f30; }
  h1, h2, h3, h4, h5, h6 {
    color: #111827;
    margin-top: 1.25em;
    margin-bottom: 0.5em;
    font-weight: 600;
    line-height: 1.3;
  }
  h1 { font-size: 1.35em; }
  h2 { font-size: 1.2em; }
  h3 { font-size: 1.1em; }
</style>
</head>
<body>${processedHtml || ''}</body>
</html>`;

    return (
        <iframe
            ref={iframeRef}
            srcDoc={srcdoc}
            className={className}
            style={{
                width: '100%',
                border: 'none',
                overflow: 'hidden',
                display: 'block',
                minHeight: '50px',
                ...style,
            }}
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            scrolling="no"
            frameBorder="0"
            title="Email content"
        />
    );
};

export default SandboxedEmail;
